/*
 * Upcoming's done-cascade (decision 357, roadmap item 23) — CLIENT math, like
 * Active's let-go rule (decision 351), and deliberately the SAME vocabulary:
 *
 *   - a fencer whose outcome is known is done and their row leaves — with no
 *     grace window, because Upcoming plans where Active spectates; the ten
 *     minutes exist so a coach sees a result land, and that moment happens on
 *     Active (and the result is already in Past, decision 345);
 *   - a fencer with NO outcome stays for the whole day they might yet fence,
 *     and leaves with the event's venue day — 351's orphan backstop, verbatim;
 *   - an event with no rows left leaves, a tournament with no events left
 *     leaves. "Tournament remains until the dates have passed or no events are
 *     left to fence": the server's date window is the first half, this cascade
 *     is the second.
 *
 * A WITHDRAWAL IS THE EXCEPTION (decision 449, amended by 775). It is an entry
 * nobody will fence, recorded before the day; §5 gives it a visible lifecycle
 * in Upcoming, so it STAYS while the event is still ahead and leaves the moment
 * it starts: a planning view has nothing to plan about an event already under
 * way, and Active owns the day-of reading from there. `placed` and `excluded`
 * leave on sight: those fencers actually fenced, and their event is over for
 * them.
 *
 * A SCRATCH IS NOT HERE ANY MORE. It is day-of FTL state, and the server no
 * longer sends it on Upcoming (decision 775): a scratched fencer's row is a
 * registered row and leaves on the venue-day backstop like any other.
 *
 * "Started" is `starts_at`, an instant, so the comparison needs no timezone at
 * all. Where no start time is published — national events publish none until
 * after the entry deadline — the venue-day backstop is the only signal left and
 * remains the fallback.
 */

import { eventDayOver } from "./activeState";
import { isDone } from "./grace";

/** The done-facts and day-facts this module reads; subsets of the Upcoming
 *  bodies so tests need no full fixture. Arrays admit null because the wire
 *  does (the generated schema types nullable arrays as `[] | null`). */
interface Entry {
  status?: string | undefined;
  outcome?: "placed" | "excluded" | undefined;
}
interface Event {
  event_date?: string | undefined;
  starts_at?: string | undefined;
  fencers?: Entry[] | null | undefined;
}
interface Tournament {
  end_date?: string | undefined;
  timezone?: string | undefined;
  events?: Event[] | null | undefined;
}

/**
 * An entry that will not be fenced: withdrawn before the day. It is retired by
 * the event's start rather than by an outcome.
 */
function isGone(f: Entry): boolean {
  return f.status === "withdrawn";
}

/**
 * Whether the event has started, which is when a gone entry stops being shown.
 *
 * Null `starts_at` returns false — not started, so a gone entry stays — and the
 * venue-day backstop retires it instead. That is the conservative direction:
 * the alternative treats "no time published" as "already begun" and hides an
 * entry on the morning a coach most wants to see it.
 */
function eventStarted(ev: Event, now: Date): boolean {
  if (!ev.starts_at) return false;
  return now.getTime() >= new Date(ev.starts_at).getTime();
}

/**
 * The payload with everything already fenced removed, ready for regroup.
 *
 * Filters BEFORE the transpose so both axes see the same world — a fencer band
 * and a tournament band must not disagree about whether an event is still to
 * fence.
 */
export function visibleUpcoming<T extends Tournament>(
  tournaments: T[] | null | undefined,
  now: Date = new Date(),
): T[] {
  return (tournaments ?? [])
    .map(
      (t) =>
        // The cast is sound because only the two arrays are replaced, each with
        // a subset of its own rows; every other field passes through the spread.
        ({
          ...t,
          events: (t.events ?? [])
            .map((ev) => ({
              ...ev,
              fencers: eventDayOver(ev, t, now)
                ? []
                : (ev.fencers ?? []).filter((f) =>
                    // A withdrawal's own gate is the event start; everyone
                    // else leaves when their outcome lands.
                    isGone(f) ? !eventStarted(ev, now) : !isDone(f),
                  ),
            }))
            .filter((ev) => ev.fencers.length > 0),
        }) as T,
    )
    .filter((t) => (t.events ?? []).length > 0);
}
